import { PharmacistLayout } from "@/components/layout/pharmacist-layout";
import { useState, useEffect, useCallback } from "react";
import { Pill, ChevronDown, ChevronUp, Loader2 } from "lucide-react";

type Status = "developing" | "achieving" | "achieved";

const STATUS_CONFIG: Record<Status, { label: string; color: string }> = {
  developing: { label: "Developing", color: "bg-slate-100 text-slate-600 border-slate-200" },
  achieving: { label: "Achieving", color: "bg-blue-50 text-blue-700 border-blue-200" },
  achieved: { label: "Achieved", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

interface Competency {
  key: string;
  label: string;
  indicator: string;
}

interface Domain {
  key: string;
  title: string;
  description: string;
  competencies: Competency[];
}

const NMP_FRAMEWORK: Domain[] = [
  {
    key: "consultation",
    title: "The Consultation",
    description: "Competencies that relate to the assessment and management of the patient/service user",
    competencies: [
      {
        key: "c1",
        label: "Assesses the presenting consultation",
        indicator: "Takes an accurate, comprehensive, patient-centred history and understands the clinical context",
      },
      {
        key: "c2",
        label: "Considers all treatment options",
        indicator: "Considers non-pharmacological approaches alongside prescribing; involves the patient in shared decision-making",
      },
      {
        key: "c3",
        label: "Reaches a shared decision",
        indicator: "Makes decisions collaboratively with the patient/carer based on best available evidence and patient preferences",
      },
      {
        key: "c4",
        label: "Prescribes safely",
        indicator: "Writes accurate, unambiguous prescriptions meeting legal and regulatory requirements; checks for interactions and contraindications",
      },
      {
        key: "c5",
        label: "Prescribes professionally",
        indicator: "Acts within professional and legal frameworks; prescribes within their competence and scope of practice",
      },
      {
        key: "c6",
        label: "Monitors and reviews treatment",
        indicator: "Establishes a monitoring plan, reviews response, and adjusts prescribing as required",
      },
    ],
  },
  {
    key: "governance",
    title: "Prescribing Governance",
    description: "Competencies that relate to the prescribing environment and professional accountability",
    competencies: [
      {
        key: "g1",
        label: "Prescribes within a governance framework",
        indicator: "Follows organisational policies, formulary restrictions, and legal requirements for independent and supplementary prescribing",
      },
      {
        key: "g2",
        label: "Manages prescribing risks",
        indicator: "Identifies, reports, and learns from prescribing incidents; applies root cause analysis principles",
      },
      {
        key: "g3",
        label: "Demonstrates accountability",
        indicator: "Maintains a clear record of prescribing decisions; is accountable for decisions made as a prescriber",
      },
      {
        key: "g4",
        label: "Manages the prescribing environment",
        indicator: "Contributes to a culture of safe prescribing; identifies and reports system failures",
      },
      {
        key: "g5",
        label: "Improves prescribing practice",
        indicator: "Participates in audit, feedback, and CPD related to prescribing; keeps up to date with guidance (BNF, NICE, local formulary)",
      },
      {
        key: "g6",
        label: "Works within own competence",
        indicator: "Recognises limitations; refers or seeks advice when outside scope of prescribing competence",
      },
    ],
  },
];

interface PortfolioEntry {
  frameworkKey: string;
  domainKey: string;
  competencyKey: string;
  status: Status;
  evidenceNotes: string | null;
}

export default function PrescribingCompetency() {
  const [entries, setEntries] = useState<Map<string, PortfolioEntry>>(new Map());
  const [saving, setSaving] = useState<string | null>(null);
  const [expandedDomain, setExpandedDomain] = useState<string | null>("consultation");
  const [editingNotes, setEditingNotes] = useState<string | null>(null);
  const [notesDraft, setNotesDraft] = useState("");

  const loadEntries = useCallback(async () => {
    const res = await fetch("/api/pharmacist/portfolio");
    if (!res.ok) return;
    const data: PortfolioEntry[] = await res.json();
    const map = new Map<string, PortfolioEntry>();
    for (const e of data) {
      if (e.frameworkKey === "nmp") {
        map.set(e.competencyKey, e);
      }
    }
    setEntries(map);
  }, []);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  const getStatus = (compKey: string): Status =>
    entries.get(compKey)?.status ?? "developing";

  const getNotes = (compKey: string): string =>
    entries.get(compKey)?.evidenceNotes ?? "";

  const upsert = async (domainKey: string, compKey: string, status: Status, evidenceNotes?: string) => {
    setSaving(compKey);
    try {
      const body: Record<string, string> = { status };
      if (evidenceNotes !== undefined) body.evidenceNotes = evidenceNotes;
      const res = await fetch(`/api/pharmacist/portfolio/nmp/${domainKey}/${compKey}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const updated: PortfolioEntry = await res.json();
        setEntries((prev) => {
          const next = new Map(prev);
          next.set(compKey, updated);
          return next;
        });
      }
    } finally {
      setSaving(null);
    }
  };

  const totalAchieved = NMP_FRAMEWORK.reduce((s, d) =>
    s + d.competencies.filter((c) => getStatus(c.key) === "achieved").length, 0);
  const total = NMP_FRAMEWORK.reduce((s, d) => s + d.competencies.length, 0);

  return (
    <PharmacistLayout title="RPS Prescribing Competency">
      <div className="space-y-6">

        {/* Header */}
        <div className="bg-gradient-to-br from-violet-900 to-violet-950 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-4">
            <Pill className="h-8 w-8 text-violet-300" />
            <div>
              <h2 className="text-xl font-bold">RPS Prescribing Competency Framework</h2>
              <p className="text-violet-300 text-sm">Competency Framework for All Prescribers (2021) · Non-Medical Prescribing Portfolio</p>
            </div>
          </div>
          <p className="text-violet-200 text-sm mb-4">
            Map your prescribing competence against the RPS Competency Framework for All Prescribers (2021). This framework applies to all prescribers regardless of profession and is used to support non-medical prescriber (NMP) portfolios, revalidation, and performance review.
          </p>
          <div className="bg-white/10 rounded-xl px-4 py-3">
            <div className="flex justify-between text-sm text-violet-200 mb-1">
              <span>Overall prescribing competency</span>
              <span>{totalAchieved}/{total} achieved</span>
            </div>
            <div className="h-2 bg-white/20 rounded-full overflow-hidden">
              <div className="h-full bg-violet-400 rounded-full transition-all" style={{ width: `${total > 0 ? Math.round((totalAchieved / total) * 100) : 0}%` }} />
            </div>
          </div>
        </div>

        {/* Domains */}
        {NMP_FRAMEWORK.map((domain) => {
          const open = expandedDomain === domain.key;
          const achieved = domain.competencies.filter((c) => getStatus(c.key) === "achieved").length;
          return (
            <div key={domain.key} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <button
                onClick={() => setExpandedDomain(open ? null : domain.key)}
                className="w-full flex items-center gap-4 px-6 py-4 text-left hover:bg-slate-50 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-slate-900">{domain.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{domain.description}</p>
                  <p className="text-xs text-slate-400 mt-1">{achieved}/{domain.competencies.length} competencies achieved</p>
                </div>
                {open ? <ChevronUp className="h-5 w-5 text-slate-400 shrink-0" /> : <ChevronDown className="h-5 w-5 text-slate-400 shrink-0" />}
              </button>

              {open && (
                <div className="border-t border-slate-100 px-6 pb-4 pt-4 space-y-4">
                  {domain.competencies.map((comp) => {
                    const status = getStatus(comp.key);
                    const notes = getNotes(comp.key);
                    const isEditing = editingNotes === comp.key;
                    const isSaving = saving === comp.key;

                    return (
                      <div key={comp.key} className="bg-slate-50 rounded-xl p-4 space-y-2">
                        <p className="text-sm font-semibold text-slate-900">{comp.label}</p>
                        <p className="text-xs text-slate-500 leading-relaxed">{comp.indicator}</p>
                        <div className="flex items-center gap-2 flex-wrap pt-1">
                          {(["developing", "achieving", "achieved"] as Status[]).map((s) => (
                            <button
                              key={s}
                              disabled={isSaving}
                              onClick={() => upsert(domain.key, comp.key, s, notes || undefined)}
                              className={`px-2.5 py-1 text-xs font-medium rounded-lg border transition-all ${
                                status === s
                                  ? STATUS_CONFIG[s].color + " ring-1 ring-offset-1 ring-current"
                                  : "bg-white border-slate-200 text-slate-500 hover:bg-slate-100"
                              }`}
                            >
                              {isSaving ? <Loader2 className="h-3 w-3 animate-spin inline" /> : STATUS_CONFIG[s].label}
                            </button>
                          ))}
                        </div>
                        {isEditing ? (
                          <div className="space-y-2 pt-1">
                            <textarea
                              value={notesDraft}
                              onChange={(e) => setNotesDraft(e.target.value)}
                              rows={3}
                              placeholder="Evidence notes — describe evidence e.g. case examples, supervisor sign-off, audit..."
                              className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={() => { upsert(domain.key, comp.key, status, notesDraft); setEditingNotes(null); }}
                                className="px-3 py-1 bg-violet-700 hover:bg-violet-800 text-white text-xs font-medium rounded-lg"
                              >Save notes</button>
                              <button
                                onClick={() => setEditingNotes(null)}
                                className="px-3 py-1 bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-medium rounded-lg"
                              >Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => { setEditingNotes(comp.key); setNotesDraft(notes); }}
                            className="text-xs text-violet-600 hover:text-violet-800 transition-colors"
                          >
                            {notes ? `📝 ${notes.slice(0, 80)}${notes.length > 80 ? "…" : ""}` : "+ Add evidence notes"}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        <div className="text-xs text-slate-400 bg-slate-50 rounded-xl px-4 py-3 leading-relaxed">
          <strong>Source:</strong> Competency Framework for All Prescribers © Royal Pharmaceutical Society 2021. This tracker supports personal NMP portfolio development. For formal independent prescriber qualification, you must complete a GPhC-approved prescribing programme and be assessed by a designated prescribing practitioner (DPP). See <a href="https://www.rpharms.com/resources/frameworks/prescribers-competency-framework" target="_blank" rel="noopener noreferrer" className="underline hover:text-violet-600">rpharms.com</a>.
        </div>
      </div>
    </PharmacistLayout>
  );
}
